import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "@hooks/useSession";
import useGetActiveSubscription from "@hooks/useGetActiveSubscription";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Stack,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { CheckCircle as CheckCircleIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";

export default function PaymentSuccess() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { session } = useSession();
  const { subscription, subscriptionLoading, subscriptionError } =
    useGetActiveSubscription();

  useEffect(() => {
    if (!session) {
      navigate("/");
      return;
    }
  }, [session, navigate]);

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "PPP");
    } catch {
      return dateString;
    }
  };

  const handleContinue = () => {
    navigate("/");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        py: 4,
        background: (theme) => ({
          backgroundImage:
            "radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))",
          ...theme.applyStyles("dark", {
            backgroundImage:
              "radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))",
          }),
        }),
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: "center",
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.background.paper,
              0.9
            )} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          {subscriptionLoading ? (
            <>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                fontWeight="bold"
              >
                {t("paymentSuccess.verifying")}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
                <CircularProgress />
              </Box>
            </>
          ) : subscriptionError ? (
            <>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                fontWeight="bold"
                color="error"
              >
                {t("paymentSuccess.verificationError")}
              </Typography>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ mb: 4, maxWidth: 400, mx: "auto" }}
              >
                {subscriptionError.message}
              </Typography>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate("/subscription")}
                sx={{ mr: 2 }}
              >
                {t("paymentSuccess.tryAgain")}
              </Button>
            </>
          ) : (
            <>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  color: "success.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 3,
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 48 }} />
              </Box>

              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                fontWeight="bold"
              >
                {t("paymentSuccess.title")}
              </Typography>

              {subscription && (
                <Stack spacing={2} sx={{ mb: 4, mt: 3 }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                    }}
                  >
                    <Typography variant="h6" gutterBottom color="info.main">
                      {t("subscription.trialPeriod")}
                    </Typography>
                    <Typography variant="body1">
                      {t("subscription.trialDays", {
                        days: subscription.trial_days,
                      })}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      {t("subscription.trialPeriodDates", {
                        start: formatDate(subscription.trial_start_date),
                        end: formatDate(subscription.trial_end_date),
                      })}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    }}
                  >
                    <Typography variant="h6" gutterBottom color="success.main">
                      {t("subscription.subscriptionPeriod")}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {t("subscription.subscriptionStart", {
                        start: formatDate(subscription.start_date),
                      })}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      {t("subscription.autoRenewalNotice")}
                    </Typography>
                  </Box>
                </Stack>
              )}

              <Button
                variant="contained"
                size="large"
                onClick={handleContinue}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(
                    theme.palette.primary.main,
                    0.8
                  )} 100%)`,
                  "&:hover": {
                    background: `linear-gradient(135deg, ${alpha(
                      theme.palette.primary.main,
                      0.9
                    )} 0%, ${alpha(theme.palette.primary.main, 0.7)} 100%)`,
                    boxShadow: theme.shadows[8],
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                {t("paymentSuccess.continueButton")}
              </Button>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
